import type { FunctionComponent } from "react";
import { Link } from "react-router-dom";
import { DropdownMenu } from "radix-ui";
import { useClerk, useUser } from "@clerk/react";
import { useUserProfile } from "../../hooks/api/useApiHooks";
import { planUsage } from "./planUsage";

interface Props {
  readonly chessUsername: string | null;
  readonly platform: string | null;
  /** Opens the link-your-chess-account modal (useChessUsername's requestPrompt). */
  readonly onLinkAccount: () => void;
}

/**
 * The signed-in right side of the top bar (#250, design/2026-10-15-home-themes r1 § The top bar, Option 1): one
 * button with the plan, the month's coached reviews and the avatar, over a menu with the linked chess account, the
 * plan, Profile, Account settings (Clerk's own screen: email, password, sessions, deleting the account) and Sign out. It replaces the linked-account badge, the plan pill and Clerk's UserButton.
 *
 * A Radix dropdown on useUser / useClerk rather than Clerk's UserButton: UserButton's trigger is the avatar alone,
 * and its custom menu items are one-line labels, so neither the plan on the button nor these two-line rows fit it.
 * Radix (the Library's filter menus use it too) gives the button aria-haspopup / aria-expanded, arrow keys, Escape,
 * outside click and focus return.
 */
export const AccountMenu: FunctionComponent<Props> = ({ chessUsername, platform, onLinkAccount }) => {
  const { user } = useUser();
  const { signOut, openUserProfile } = useClerk();
  const { data: profile } = useUserProfile();
  const plan = profile ? planUsage(profile) : null;
  const avatarUrl = user?.imageUrl ?? null;

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger
        data-account
        aria-label={plan ? `Account menu: ${plan.title}, ${plan.line}` : "Account menu"}
        className={`inline-flex h-8 shrink-0 items-center gap-2 rounded-full bg-white/[0.05] pr-[3px] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)] transition-colors duration-150 hover:bg-white/[0.08] data-[state=open]:bg-white/[0.08] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] motion-reduce:transition-none ${plan ? "pl-[3px] sm:pl-[11px]" : "pl-[3px]"}`}
      >
        {/* Under sm the button is the avatar alone (#262): the plan and the count stay in the menu and the label. */}
        {plan && (
          <span className="whitespace-nowrap text-xs font-medium text-faint max-sm:hidden">
            {plan.label}
            <b className={`ml-[3px] font-mono font-semibold tabular-nums ${plan.atLimit ? "text-danger" : "text-subdued"}`}>{plan.count}</b>
          </span>
        )}
        <Avatar url={avatarUrl} size={26} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="z-[1100] w-[300px] max-w-[calc(100vw-16px)] rounded-[12px] border border-[var(--glass-border)] bg-[var(--glass-bg)] p-1.5 shadow-[var(--glass-shadow)] outline-none"
        >
          <DropdownMenu.Item asChild className={`${itemClass} mb-1 rounded-b-none border-b border-white/[0.07]`}>
            <button type="button" onClick={onLinkAccount}>
              <Avatar url={avatarUrl} size={28} />
              {chessUsername ? (
                <Rows title={chessUsername} line={`${platform === "lichess" ? "Lichess" : "Chess.com"} · linked`} />
              ) : (
                <Rows title="No chess account" line="Link one to bring in your games" />
              )}
              <span className="shrink-0 text-xs font-semibold text-subdued">{chessUsername ? "Change" : "Link account"}</span>
            </button>
          </DropdownMenu.Item>
          {plan && (
            <DropdownMenu.Item asChild className={itemClass}>
              <Link to={plan.action.to}>
                <Rows title={plan.title} line={plan.line} />
                <span className={`shrink-0 text-xs font-semibold ${plan.action.label === "Upgrade" ? "text-link" : "text-subdued"}`}>
                  {plan.action.label}
                </span>
              </Link>
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item asChild className={itemClass}>
            <Link to="/profile">
              <b className="font-semibold text-on-surface">Profile</b>
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild className={itemClass}>
            {/* the only way into Clerk's account screen since UserButton went: email, password, sessions, delete account */}
            <button type="button" onClick={() => openUserProfile()}>
              <b className="font-semibold text-on-surface">Account settings</b>
            </button>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild className={itemClass}>
            {/* afterSignOutUrl="/" on the ClerkProvider (index.tsx) takes it home, as UserButton's sign-out did. */}
            <button type="button" onClick={() => void signOut()}>
              <b className="font-semibold text-on-surface">Sign out</b>
            </button>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

// Radix highlights the item under the pointer or the arrow keys (it holds focus); the inset ring marks keyboard focus.
const itemClass =
  "flex w-full cursor-pointer select-none items-center gap-2.5 rounded-lg px-2.5 py-[9px] text-left text-[13px] text-[var(--color-text-prose)] no-underline outline-none data-[highlighted]:bg-white/[0.05] focus-visible:shadow-[inset_0_0_0_1.5px_var(--color-accent)]";

const Rows: FunctionComponent<{ readonly title: string; readonly line: string }> = ({ title, line }) => (
  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
    <b className="truncate font-semibold text-on-surface">{title}</b>
    <span className="text-[11.5px] text-faint">{line}</span>
  </span>
);

const Avatar: FunctionComponent<{ readonly url: string | null; readonly size: 26 | 28 }> = ({ url, size }) => {
  const box = size === 26 ? "h-[26px] w-[26px]" : "h-7 w-7";
  return url ? (
    <img src={url} alt="" width={size} height={size} className={`${box} shrink-0 rounded-full object-cover`} />
  ) : (
    <span aria-hidden="true" className={`${box} shrink-0 rounded-full bg-[var(--color-bg-elevated)]`} />
  );
};
